import axios, { AxiosHeaders, InternalAxiosRequestConfig } from "axios";
import { getToken, isTokenExpired, clearToken } from "../services/AuthService";
import { apiContextPath, getChainId } from "../config/tenant";

function resolveApiBaseUrl(): string {
	const path = `${apiContextPath()}/api/v1/checkin`;
	if (
		process.env.NODE_ENV === "development" &&
		process.env.REACT_APP_API_BASE_URL
	) {
		try {
			const u = new URL(process.env.REACT_APP_API_BASE_URL);
			return `${u.origin}${path}`;
		} catch {
			return process.env.REACT_APP_API_BASE_URL!;
		}
	}
	return path;
}

const apiClient = axios.create({
	baseURL: resolveApiBaseUrl(),
	timeout: 60000,
});

apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		if (isTokenExpired()) {
			console.warn("Token expired. Logging out...");
			clearToken();
			const base = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
			window.location.href = base ? `${base}/` : "/";
			return Promise.reject("Token expired");
		}

		if (!config.headers) {
			config.headers = new AxiosHeaders();
		}
		const h = config.headers as AxiosHeaders;

		// Attach the stable chain UUID resolved at startup. Sending chainId (not chainCode)
		// means renaming the chain in the dashboard never invalidates this in-flight session:
		// the backend filter resolves the chain by id and the session JWT's chainId claim
		// continues to match the request scope. The specific hotel within the chain is encoded
		// in the session JWT (set after the FetchBooking response), so no per-request hotel
		// header is needed here.
		try {
			h.set("chainId", getChainId());
		} catch {
			// tenant not yet initialized -- chain-info calls use a separate axios instance
		}

		const token = getToken();
		if (token) {
			h.set("Authorization", `Bearer ${token}`);
		}

		return config;
	},
	(error) => Promise.reject(error)
);

export default apiClient;
